import * as React from 'react';

import { cn } from '../../../lib/utils';

/** The shadcn/ui Card parts. Plain elements in shadcn too, so there is no Radix package behind them.
 *
 *  The anatomy is shadcn's: a column of header, content and footer with the header's grid reserving a
 *  trailing column when a `CardAction` is present. The styling reads this app's tokens instead: the ground
 *  is `--color-raised` (a lifted surface even where a design paints its ordinary cards into the page), the
 *  edge is the `hairline` ink, the depth is the design's own `--shadow-card`, and the radius is the scale's
 *  `xl`. A recess inside the card — the muted footer, a muted record — is `--color-sunken`.
 *
 *  The header's action sits beside the title from the header's `@sm` width up, and moves under the
 *  description below it: beside a phone-width title it would leave the sentence one word wide.
 *
 *  The inset steps down from shadcn's 24px to 16px below the `sm` breakpoint, where a card's content
 *  would otherwise lose a sixth of a 320px screen to padding.
 *
 *  One deliberate addition: `CardFooter variant="muted"` is the settings strip that closes a card — a
 *  quiet band with the hint on the left and the primary action on the right. The card gives up its bottom
 *  padding for it, so the band sits flush on the card's edge instead of floating above an empty margin.
 *  The card never clips (`overflow-hidden`) to round that band: menus and pickers render in place in this
 *  app (no portals), and a clipping card would cut them off. The band rounds its own corners instead. */

function Card({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card"
      className={cn(
        'flex min-w-0 flex-col gap-5 rounded-xl border border-hairline bg-raised py-5 text-raised-foreground shadow-card sm:gap-6 sm:py-6',
        'has-[>[data-slot=card-footer][data-variant=muted]]:pb-0',
        className,
      )}
      {...props}
    />
  );
}

function CardHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        '@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 px-4 sm:px-6 has-data-[slot=card-action]:grid-cols-[minmax(0,1fr)_auto] [.border-b]:pb-6',
        // Title and sentence hold the first column, so auto-placement never drops the sentence into the
        // action's column while the action sits under them on a narrow header.
        '[&>[data-slot=card-description]]:col-start-1 [&>[data-slot=card-title]]:col-start-1',
        className,
      )}
      {...props}
    />
  );
}

function CardTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-title"
      className={cn('flex min-w-0 items-center gap-2 text-section-title tracking-ui', className)}
      {...props}
    />
  );
}

function CardDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-description"
      className={cn('max-w-[var(--measure-prose)] text-meta tracking-ui text-muted-foreground', className)}
      {...props}
    />
  );
}

function CardAction({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        'col-start-1 row-start-3 w-full justify-self-start',
        '@sm/card-header:col-start-2 @sm/card-header:row-span-2 @sm/card-header:row-start-1 @sm/card-header:w-auto @sm/card-header:self-start @sm/card-header:justify-self-end',
        className,
      )}
      {...props}
    />
  );
}

function CardContent({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="card-content" className={cn('min-w-0 px-4 sm:px-6', className)} {...props} />;
}

function CardFooter({ className, variant = 'default', ...props }: React.ComponentProps<'div'> & { variant?: 'default' | 'muted' }) {
  return (
    <div
      data-slot="card-footer"
      data-variant={variant}
      className={cn(
        'flex items-center px-4 sm:px-6 [.border-t]:pt-6',
        'data-[variant=muted]:flex-wrap data-[variant=muted]:gap-x-4 data-[variant=muted]:gap-y-3 data-[variant=muted]:[&>:last-child]:ml-auto',
        'data-[variant=muted]:rounded-b-[inherit] data-[variant=muted]:border-t data-[variant=muted]:border-hairline data-[variant=muted]:bg-sunken data-[variant=muted]:py-3.5',
        className,
      )}
      {...props}
    />
  );
}

export { Card, CardAction, CardContent, CardDescription, CardFooter, CardHeader, CardTitle };
